import { Injectable } from "@angular/core";
import { formatDate } from "@angular/common";
import PouchDB from "pouchdb";
import { NetworkService } from "./network.service";
import { TimezoneService } from "./timezone.service";

@Injectable({ providedIn: "root" })
export class DataService {
  private db: PouchDB.Database;
  private dbNote: PouchDB.Database<{}>;
  private dbScreenShot: PouchDB.Database<{}>;
  private dbWhiteBoard: PouchDB.Database<{}>;

  constructor(
    private network: NetworkService,
    private timezoneService: TimezoneService,
  ) {
    this.db = new PouchDB("ZEUS-db");
    this.dbNote = new PouchDB("notes_db");
    this.dbScreenShot = new PouchDB("SCREENSHOT_db");
    this.dbWhiteBoard = new PouchDB("whiteBoard_db");
  }

  private getDocId(key: string): string {
    return `${key}_1`;
  }

  /**
   * Generic upsert helper that retries on 409 write conflicts, since "conflict"
   * errors happen when the `_rev` used for `put()` goes stale between `get()` and `put()`.
   * `diffFun` receives the existing doc (or `{}` if not found) and must return the
   * new doc contents, or a falsy value to abort without writing.
   */
  private async upsert(
    db: PouchDB.Database<any>,
    docId: string,
    diffFun: (doc: any) => Record<string, any> | false | null | undefined,
  ): Promise<{ updated: boolean; rev?: string; id: string }> {
    for (;;) {
      let doc: any = {};
      try {
        doc = await db.get(docId);
      } catch (err: any) {
        if (err.status !== 404) {
          throw err;
        }
      }

      const newDoc = diffFun(doc);
      if (!newDoc) {
        return { updated: false, id: docId };
      }

      try {
        const result = await db.put({ ...newDoc, _id: docId, _rev: doc._rev });
        return { updated: true, rev: result.rev, id: docId };
      } catch (err: any) {
        if (err.status === 409) {
          continue; // stale _rev, refetch and retry
        }
        throw err;
      }
    }
  }

  public destroyDB(): Promise<string> {
    const res = this.db
      .allDocs()
      .then(function (result: { rows: any[] }) {
        return Promise.all(
          result.rows.map(function (row: {
            id: PouchDB.Core.RemoveDocument;
            value: { rev: PouchDB.Core.Options | undefined };
          }) {
            let database2 = new PouchDB("ZEUS-db");
            return database2.remove(row.id, row.value.rev);
          })
        );
      })
      .catch(function (err: any) {
        return err;
      });
    return res;
  }

  async saveToLocalForDownload(
    key: string,
    newDoc: any
  ): Promise<"duplicate" | "saved" | "created" | "error"> {
    const docId = this.getDocId(key);
    let status: "duplicate" | "saved" | "created" | "error" = "error";

    try {
      await this.upsert(this.db, docId, (existing: any) => {
        if (!existing._id) {
          status = "created";
          return { data: [newDoc] };
        }

        if (Array.isArray(existing.data)) {
          const exists = existing.data.some((item: any) => item.id === newDoc.id);
          if (exists) {
            status = "duplicate";
            return false;
          }
          status = "saved";
          return { data: [...existing.data, newDoc] };
        }

        status = "saved";
        return { data: [existing.data, newDoc] };
      });

      return status;
    } catch (err: any) {
      console.error("[PouchDB] Error in saveToLocal:", err);
      return "error";
    }
  }

  async getFromLocalDownload(key: string): Promise<any> {
    const docId = this.getDocId(key);
    try {
      const doc: any = await this.db.get(docId);
      return { data: doc.data, pdb: true };
    } catch (err) {
      console.warn("[PouchDB] getFromLocal failed:", err);
      return { error: err, pdb: false };
    }
  }

  async deleteFromLocalDownload(key: string, id: number): Promise<void> {
    const docId = this.getDocId(key);

    try {
      await this.upsert(this.db, docId, (existing: any) => {
        if (!existing._id || !Array.isArray(existing.data)) {
          console.warn("[PouchDB] Data is not an array, cannot delete");
          return false;
        }
        return { data: existing.data.filter((item: any) => item.id !== id) };
      });
    } catch (err) {
      console.error("[PouchDB] Error in deleteFromLocal:", err);
    }
  }

  async saveToLocal(key: string, data: any): Promise<void> {
    const docId = this.getDocId(key);

    try {
      await this.upsert(this.db, docId, (existing: any) => ({ ...existing, ...data }));
    } catch (err) {
      console.error("[PouchDB] Error in saveToLocal:", err);
    }
  }

  async getFromLocal(key: string): Promise<any> {
    const docId = this.getDocId(key);
    try {
      const doc: any = await this.db.get(docId);
      return doc;
    } catch (err) {
      console.warn("[PouchDB] getFromLocal failed:", err);
      return null; 
    }
  }

  async smartFetch(key: string, apiFn: () => Promise<any>): Promise<any> {
    const isOnline = await this.network.getStatus();
    if (isOnline) {
      try {
        const result = await apiFn();
        
        if (result.code === 200) {
          this.saveToLocal(key, result);
        }
        return result;
      } catch (err) {
        return await this.getFromLocal(key); 
      }
    } else {
      return await this.getFromLocal(key); 
    }
  }

  async addNote(note: any) {
    note._id = formatDate(
      new Date(),
      "yyyyMMddHHmmssSSS",
      "en-GB",
      this.timezoneService.timezone,
    );
    return this.dbNote.put(note);
  }

  async getAllNotes() {
    const result = await this.dbNote.allDocs({ include_docs: true });
    return result.rows.map((row: any) => row.doc);
  }

  async deleteNote(id: string, rev: string) {
    return this.dbNote.remove(id, rev);
  }

  async clearSyncedNotes() {
    const notes = await this.getAllNotes();
    for (const note of notes) {
      if (note.synced) {
        await this.deleteNote(note._id, note._rev);
      }
    }
  }

  async addScreenShot(ScreenShot: any) {
    ScreenShot._id = formatDate(
      new Date(),
      "yyyyMMddHHmmssSSS",
      "en-GB",
      this.timezoneService.timezone,
    );
    return this.dbScreenShot.put(ScreenShot);
  }

  async getAllScreenShots() {
    const result = await this.dbScreenShot.allDocs({ include_docs: true });
    return result.rows.map((row: any) => row.doc);
  }

  async deleteScreenShot(id: string, rev: string) {
    return this.dbScreenShot.remove(id, rev);
  }

  async clearSyncedScreenShots() {
    const ScreenShots = await this.getAllScreenShots();
    for (const ScreenShot of ScreenShots) {
      if (ScreenShot.synced) {
        await this.deleteScreenShot(ScreenShot._id, ScreenShot._rev);
      }
    }
  }

  async saveToLocalWhiteBoard(
    key: string,
    newData: any
  ): Promise<{ success: boolean; result?: any; error?: any }> {
    const docId = this.getDocId(key);

    try {
      const result = await this.upsert(this.dbWhiteBoard, docId, (existing: any) => ({
        alarmId: newData.alarmId,
        files: Array.isArray(existing.files) ? [...existing.files, newData] : [newData],
      }));
      return { success: true, result };
    } catch (err) {
      console.error("[PouchDB] Error in saveToLocal:", err);
      return { success: false, error: err };
    }
  }

  private parseTimeToMillis(value: unknown): number {
    if (value === null || value === undefined) return 0;

    const raw = String(value);
    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);
    const tz = this.timezoneService.timezone;
    const tzWithColon = `${tz.slice(0, 3)}:${tz.slice(3)}`;
    const safeValue = hasTimezone ? normalized : `${normalized}${tzWithColon}`;

    const millis = new Date(safeValue).getTime();
    return isNaN(millis) ? 0 : millis;
  }

  async getWhiteboardByAlarmId(alarmId: string): Promise<any[]> {
    const docId = this.getDocId(`WHITEBOARDIMAGE${alarmId}`);

    try {
      const doc: any = await this.dbWhiteBoard.get(docId);
      const files = doc.files || [];

      return files.sort(
        (a: any, b: any) =>
          this.parseTimeToMillis(b.createdAt) - this.parseTimeToMillis(a.createdAt)
      );
    } catch (err: any) {
      if (err.status === 404) {
        console.warn(`[PouchDB] No images found for alarmId: ${alarmId}`);
        return [];
      }
      console.error("[PouchDB] Error in getWhiteboardByAlarmId:", err);
      return [];
    }
  }
}
